import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { safeFetch } from "../../lib/safeFetch";

const STORAGE_KEY = "maxlive_popup_seen_v2";

function loadSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function saveSeen(map) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/**
 * Renders the first enabled admin-configured popup matching the audience.
 * Supported `style` values (set by admin):
 *   - "modal"          → centered overlay with backdrop (default)
 *   - "banner_top"     → full-width banner stuck to the top of the viewport
 *   - "banner_bottom"  → full-width banner stuck to the bottom
 *   - "toast"          → small bottom-right card (mobile: bottom-center)
 *
 * Honours `delay_seconds`, `dismissible`, `show_once_per_day`, `audience`,
 * `placement`, `starts_at`, `ends_at` — all controlled from admin panel.
 */
export default function PopupBanner({ placement = "home" }) {
  const [popup, setPopup] = useState(null);
  const [visible, setVisible] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    let cancelled = false;
    let showTimer = null;
    let delayTimer = null;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    async function run() {
      try {
        const res = await safeFetch(`${BACKEND_URL}/api/public/popups`, { cache: "no-store" });
        const items = await res.json();
        if (cancelled || !Array.isArray(items)) return;
        const isLoggedIn = !!user?.id;
        const onHome = typeof window !== "undefined" && window.location.pathname === "/";
        const matching = items.filter((p) => {
          if (!p.enabled) return false;
          if (p.placement === "home" && !onHome) return false;
          if (p.audience === "guest" && isLoggedIn) return false;
          if (p.audience === "logged_in" && !isLoggedIn) return false;
          return true;
        });
        const seen = loadSeen();
        const today = todayKey();
        const next = matching.find((p) => {
          if (!p.show_once_per_day) return true;
          // Use id + updated_at as the "shown" key — every admin edit OR
          // explicit "force reshow" bumps updated_at, so visitors see the
          // popup again immediately without waiting 24 h.
          const ver = p.updated_at || p.created_at || "";
          const key = `${p.id}:${ver}:${today}`;
          return !seen[key];
        });
        if (next) {
          setPopup(next);
          // Honour admin's "show after X seconds" delay
          const delayMs = Math.max(0, Number(next.delay_seconds || 0)) * 1000;
          delayTimer = setTimeout(() => {
            if (cancelled) return;
            setVisible(true);
            // Fire-and-forget impression event
            try {
              safeFetch(`${BACKEND_URL}/api/public/popups/${next.id}/view`, {
                method: "POST",
                cache: "no-store",
                keepalive: true,
                timeoutMs: 5000,
              });
            } catch { /* ignore */ }
          }, delayMs);
        }
      } catch {
        /* ignore */
      }
    }
    showTimer = setTimeout(run, 600);
    return () => {
      cancelled = true;
      clearTimeout(showTimer);
      clearTimeout(delayTimer);
    };
  }, [placement, user?.id]);

  const dismiss = () => {
    if (popup?.show_once_per_day) {
      const seen = loadSeen();
      const ver = popup.updated_at || popup.created_at || "";
      seen[`${popup.id}:${ver}:${todayKey()}`] = 1;
      saveSeen(seen);
    }
    setVisible(false);
    // Allow exit animation
    setTimeout(() => setPopup(null), 220);
  };

  const trackClick = () => {
    if (!popup) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      safeFetch(`${BACKEND_URL}/api/public/popups/${popup.id}/click`, {
        method: "POST",
        cache: "no-store",
        keepalive: true,
        timeoutMs: 5000,
      });
    } catch { /* ignore */ }
  };

  if (!popup || !visible) return null;

  const style = popup.style || "modal";
  const dismissible = popup.dismissible !== false;
  const accent = popup.accent_color || "#10B981";

  // ----- Banner styles (top / bottom) -----
  if (style === "banner_top" || style === "banner_bottom") {
    const pos = style === "banner_top" ? "top-0" : "bottom-0";
    const animClass = style === "banner_top" ? "animate-[slideDownIn_.35s_ease]" : "animate-[slideUpIn_.35s_ease]";
    return (
      <div
        data-testid="site-popup"
        data-popup-style={style}
        className={`fixed left-0 right-0 ${pos} z-[80] ${animClass}`}
        style={{ background: popup.background || "#0F172A", color: popup.text_color || "#FFFFFF" }}
      >
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          {popup.image && (
            <img src={popup.image} alt="" className="hidden sm:block h-10 w-10 rounded-md object-cover flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            {popup.title && <div className="font-semibold text-sm sm:text-base truncate">{popup.title}</div>}
            {popup.body && <div className="text-xs sm:text-sm opacity-90 line-clamp-2">{popup.body}</div>}
          </div>
          {popup.cta_text && popup.cta_link && (
            <a
              href={popup.cta_link}
              onClick={(e) => { trackClick(); if (dismissible) dismiss(); }}
              data-testid="site-popup-cta"
              className="flex-shrink-0 inline-flex items-center justify-center h-9 px-4 rounded-full text-xs sm:text-sm font-bold transition-transform hover:-translate-y-0.5"
              style={{ background: accent, color: "#0B1220" }}
            >
              {popup.cta_text}
            </a>
          )}
          {dismissible && (
            <button
              onClick={dismiss}
              data-testid="site-popup-close"
              className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-white/10"
              aria-label="close popup"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
    );
  }

  // ----- Toast style (small bottom-right card) -----
  if (style === "toast") {
    return (
      <div
        data-testid="site-popup"
        data-popup-style="toast"
        className="fixed z-[80] bottom-4 right-4 sm:right-6 max-w-xs w-[calc(100%-2rem)] sm:w-80 rounded-xl shadow-2xl border border-white/10 overflow-hidden animate-[slideUpIn_.35s_ease]"
        style={{ background: popup.background || "#0F172A", color: popup.text_color || "#FFFFFF" }}
      >
        {dismissible && (
          <button
            onClick={dismiss}
            data-testid="site-popup-close"
            className="absolute top-2 right-2 z-10 h-7 w-7 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm"
            aria-label="close popup"
          >
            <X size={12} />
          </button>
        )}
        {popup.image && (
          <div className="h-28 overflow-hidden">
            <img src={popup.image} alt={popup.title || ""} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-4">
          {popup.title && <h4 className="font-bold text-sm mb-1 leading-tight pr-6">{popup.title}</h4>}
          {popup.body && <p className="text-[12px] leading-relaxed opacity-90 line-clamp-3">{popup.body}</p>}
          {popup.cta_text && popup.cta_link && (
            <a
              href={popup.cta_link}
              onClick={(e) => { trackClick(); dismiss(); }}
              data-testid="site-popup-cta"
              className="mt-3 inline-flex items-center justify-center w-full h-9 rounded-full font-semibold text-[13px]"
              style={{ background: accent, color: "#0B1220" }}
            >
              {popup.cta_text}
            </a>
          )}
        </div>
      </div>
    );
  }

  // ----- Modal style (default) -----
  return (
    <div
      data-testid="site-popup"
      data-popup-style="modal"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-[fadeIn_.25s_ease]"
      onClick={dismissible ? dismiss : undefined}
    >
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl border border-white/10 animate-[popIn_.3s_cubic-bezier(.34,1.56,.64,1)]"
        style={{ background: popup.background || "#ffffff", color: popup.text_color || "#111827" }}
        onClick={(e) => e.stopPropagation()}
      >
        {dismissible && (
          <button
            onClick={dismiss}
            className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/30 hover:bg-black/50 text-white flex items-center justify-center backdrop-blur-sm"
            data-testid="site-popup-close"
            aria-label="close popup"
          >
            <X size={16} />
          </button>
        )}
        {popup.image ? (
          <div className="h-56 relative overflow-hidden">
            <img src={popup.image} alt={popup.title || ""} className="w-full h-full object-cover" />
          </div>
        ) : null}
        <div className="p-6 sm:p-7">
          {popup.title && (
            <h3 className="text-2xl font-bold mb-2 leading-tight">{popup.title}</h3>
          )}
          {popup.body && (
            <p className="text-sm leading-relaxed opacity-90 whitespace-pre-line">{popup.body}</p>
          )}
          {popup.cta_text && popup.cta_link && (
            <a
              href={popup.cta_link}
              onClick={(e) => { trackClick(); if (dismissible) dismiss(); }}
              className="mt-5 inline-flex items-center justify-center w-full h-11 rounded-full font-semibold text-sm transition-transform hover:-translate-y-0.5"
              style={{ background: accent, color: "#0B1220" }}
              data-testid="site-popup-cta"
            >
              {popup.cta_text}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
